/**
 * @file departmentService.js
 * @description Department admin API. Records are scoped to the session client by the authorizer,
 * so no client code is sent. deptSort is the department number and is always sent as a string
 * ("060" and "60" are different records).
 */
import { getToken } from 'utils/helper';
import { logger } from 'utils/logger';

const getUrl = () => `${process.env.REACT_APP_API_URL}/department/admin`;

// fields the API allows us to write (deptSort is the key and can't be changed)
export const DEPARTMENT_WRITABLE_FIELDS = ['name', 'defaultSize', 'eventName', 'productCategory', 'productSubcategory'];

/**
 * Parses the response and throws the API error message for non 2xx responses.
 */
const handleResponse = async (response) => {
    let data = null;
    try {
        data = await response.json();
    } catch (err) {
        data = null;
    }

    if (!response.ok) {
        throw new Error(data?.error ?? `Request failed (${response.status})`);
    }
    return data;
};

/**
 * Returns every department for the session client in deptSort order.
 */
export const getDepartments = async () => {
    return fetch(getUrl(), {
        headers: {
            hgio: getToken(),
        },
        method: 'GET',
    })
        .then(handleResponse)
        .then((data) => {
            logger.log(data);
            return (data?.Items ?? []).map((item) => ({ ...item, id: item.deptSort }));
        })
        .catch((err) => {
            logger.error('ERROR getting departments', err);
            throw err;
        });
};

/**
 * Creates the department when deptSort is new, otherwise updates it.
 * Only writable fields are sent; a field set to '' is cleared by the API.
 */
export const saveDepartment = async (department) => {
    const payload = { deptSort: String(department.deptSort ?? '').trim() };
    DEPARTMENT_WRITABLE_FIELDS.forEach((key) => {
        if (department[key] !== undefined && department[key] !== null) {
            payload[key] = String(department[key]);
        }
    });

    return fetch(getUrl(), {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify(payload),
    })
        .then(handleResponse)
        .then((data) => {
            logger.log(data);
            return { ...data, id: data.deptSort };
        })
        .catch((err) => {
            logger.error('ERROR saving department', err);
            throw err;
        });
};

/**
 * Deletes one department. Existing event-style records are not changed.
 */
export const deleteDepartment = async (deptSort) => {
    const url = `${getUrl()}?deptSort=${encodeURIComponent(deptSort)}`;

    return fetch(url, {
        headers: {
            hgio: getToken(),
        },
        method: 'DELETE',
    })
        .then(handleResponse)
        .then((data) => {
            logger.log(data);
            return { ...data, id: data.deptSort };
        })
        .catch((err) => {
            logger.error('ERROR deleting department', err);
            throw err;
        });
};
